import { AudioBus } from "../audio";
import {
  COYOTE,
  ENERGY_DRAIN,
  ENERGY_MAX,
  ENERGY_REFILL,
  ENERGY_START,
  FALL_DURATION,
  GRAVITY,
  HOLD_GRAVITY,
  JUMP_BUFFER,
  JUMP_V,
  MIN_JUMP_V,
  LAND_LOCK,
  PLAYER_H,
  STUMBLE_TIME,
  WEAK_JUMP_SCALE,
} from "./constants";
import { Input } from "./input";
import { Particles } from "./particles";
import {
  drawFarHouses,
  drawGround,
  drawHills,
  drawHouse,
  drawHudGlow,
  drawPlayer,
  drawSky,
  resizeCanvas,
} from "./render";
import { applyLanding } from "./score";
import type { GameMode, House, Player, RunStats } from "./types";
import { Neighborhood, obstacleWorldX, roofBounds, runSpeed } from "./world";

export interface GameHooks {
  onHud: (stats: RunStats, speed: number) => void;
  onGameOver: (stats: RunStats) => void;
}

export class Game {
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  input: Input;
  audio: AudioBus;
  hooks: GameHooks;
  mode: GameMode = "start";
  stats: RunStats = emptyStats();
  private world = new Neighborhood();
  private player: Player = spawnPlayer(80, 400);
  private particles = new Particles();
  private cameraX = 0;
  private viewW = 1280;
  private last = 0;
  private raf = 0;
  private landLock = 0;
  private shake = 0;
  private time = 0;
  private running = false;
  private moving = false;
  private goLock = 0;
  private releasedSinceGo = true;
  private auto = false;
  private autoHold = false;

  constructor(canvas: HTMLCanvasElement, audio: AudioBus, hooks: GameHooks) {
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas is not available");
    this.canvas = canvas;
    this.ctx = ctx;
    this.audio = audio;
    this.hooks = hooks;
    this.input = new Input(canvas.parentElement ?? canvas);
    this.loop = this.loop.bind(this);
  }

  start(opts?: { silent?: boolean }): void {
    this.world = new Neighborhood();
    this.stats = emptyStats();
    const first = this.world.houses[0];
    this.player = spawnPlayer((first?.x ?? 40) + 70, (first?.roofY ?? 448) - PLAYER_H / 2);
    this.player.onRoof = true;
    this.player.houseId = first?.id ?? 1;
    this.cameraX = this.player.x - 220;
    this.mode = "playing";
    this.landLock = 0.15;
    this.moving = false;
    this.goLock = 0;
    this.releasedSinceGo = true;
    this.input.clearJump();
    if (first && !first.lit) this.landOn(first);
    this.shake = 0;
    this.time = 0;
    this.particles = new Particles();
    this.viewW = 1280;
    if (!opts?.silent) this.ensureLoop();
    this.hooks.onHud(this.stats, runSpeed(0));
    if (this.auto) {
      this.moving = true;
      this.releasedSinceGo = true;
    }
  }

  enableAuto(): void {
    this.auto = true;
  }

  /** Headless helper for tests. Does not start requestAnimationFrame. */
  stepSilent(seconds: number, dt = 1 / 60): RunStats {
    this.viewW = 1280;
    for (let t = 0; t < seconds; t += dt) {
      this.time += dt;
      if (this.mode === "falling") this.updateFall(dt);
      else if (this.mode === "playing") this.updatePlay(dt);
      else if (this.mode === "over") break;
    }
    return this.stats;
  }

  stop(): void {
    this.running = false;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
  }

  dispose(): void {
    this.stop();
    this.input.dispose();
  }

  preview(): void {
    this.mode = "start";
    this.ensureLoop();
  }

  private ensureLoop(): void {
    if (this.running) return;
    this.running = true;
    this.last = performance.now();
    this.raf = requestAnimationFrame(this.loop);
  }

  private loop(now: number): void {
    if (!this.running) return;
    const dt = Math.min(0.033, (now - this.last) / 1000);
    this.last = now;
    this.time += dt;
    this.tick(dt);
    this.draw();
    this.raf = requestAnimationFrame(this.loop);
  }

  private tick(dt: number): void {
    const { w } = resizeCanvas(this.canvas);
    this.viewW = w;

    if (this.mode === "start") {
      this.cameraX += 18 * dt;
      this.world.ensureAhead(this.cameraX, this.viewW, 4);
      this.particles.update(dt, this.cameraX, this.viewW, 720, -20);
      return;
    }

    if (this.mode === "over") {
      this.particles.update(dt, this.cameraX, this.viewW, 720, -10);
      return;
    }

    if (this.mode === "falling") {
      this.updateFall(dt);
      this.particles.update(dt, this.cameraX, this.viewW, 720, -30);
      return;
    }

    this.updatePlay(dt);
    this.particles.update(dt, this.cameraX, this.viewW, 720, -24 - this.stats.housesLit);
    this.hooks.onHud(this.stats, runSpeed(this.stats.housesLit));
  }

  private updatePlay(dt: number): void {
    const p = this.player;
    if (!this.moving) {
      p.vx = 0;
      p.vy = 0;
      p.onRoof = true;
      p.runT += dt * 0.35;
      if (this.input.consumeJump()) {
        this.moving = true;
        this.goLock = 0.4;
        this.landLock = 0.4;
        this.releasedSinceGo = false;
        this.input.clearJump();
      }
      this.hooks.onHud(this.stats, 0);
      return;
    }
    this.goLock = Math.max(0, this.goLock - dt);
    if (!this.input.held) this.releasedSinceGo = true;
    if (this.goLock > 0 || !this.releasedSinceGo) this.input.clearJump();
    const speed = runSpeed(this.stats.housesLit);
    p.vx = speed;
    if (this.auto) this.autoPilot();
    this.landLock = Math.max(0, this.landLock - dt);
    p.stumble = Math.max(0, p.stumble - dt);
    this.shake = Math.max(0, this.shake - dt);

    const drain = ENERGY_DRAIN * (0.7 + speed / 400) * dt;
    this.stats.energy = Math.max(0, this.stats.energy - drain);

    if (!this.auto && this.goLock <= 0 && this.releasedSinceGo && this.input.consumeJump()) {
      p.jumpBuffer = JUMP_BUFFER;
    } else {
      p.jumpBuffer = Math.max(0, p.jumpBuffer - dt);
    }
    if (this.auto) this.input.clearJump();

    if (p.onRoof) p.coyote = COYOTE;
    else p.coyote = Math.max(0, p.coyote - dt);

    if (p.jumpBuffer > 0 && p.coyote > 0 && p.stumble <= 0 && this.landLock <= 0) {
      this.doJump();
    }

    p.jumpHeld = this.input.held || this.autoHold;
    const grav = p.vy < 0 && p.jumpHeld ? HOLD_GRAVITY : GRAVITY;
    if (!p.onRoof) {
      p.vy += grav * dt;
      if (!p.jumpHeld && p.vy < -MIN_JUMP_V) p.vy = -MIN_JUMP_V;
    } else {
      p.vy = 0;
    }
    if (p.vy >= 0) this.autoHold = false;

    p.x += p.vx * dt * (p.stumble > 0 ? 0.72 : 1);
    p.y += p.vy * dt;
    p.runT += dt;
    p.rotation = p.onRoof ? 0 : Math.max(-0.35, Math.min(0.45, p.vy * 0.00045));

    this.world.ensureAhead(this.cameraX, this.viewW, this.stats.housesLit);
    this.world.prune(this.cameraX);

    this.resolveRoofs(dt);
    this.resolveObstacles();

    if (p.y > 640) this.beginFall("miss");

    const targetCam = p.x - this.viewW * 0.3;
    this.cameraX += (targetCam - this.cameraX) * Math.min(1, dt * 8);
  }

  private autoPilot(): void {
    const p = this.player;
    if (this.mode !== "playing" || !p.onRoof || this.landLock > 0) return;
    const house = this.world.houseById(p.houseId);
    if (!house) return;
    const b = roofBounds(house);
    const speed = runSpeed(this.stats.housesLit);
    const takeoff = Math.max(64, speed * 0.34);
    if (b.right - p.x < takeoff && b.right - p.x > 6) {
      this.doJump();
      return;
    }
    for (const o of house.obstacles) {
      if (o.kind === "ice" || o.clipped) continue;
      const ox = obstacleWorldX(house, o, this.time);
      if (ox - p.x > 8 && ox - p.x < 56) {
        this.doJump();
        return;
      }
    }
  }

  private doJump(): void {
    const p = this.player;
    const weak = this.stats.energy <= 0.5;
    p.vy = -JUMP_V * (weak ? WEAK_JUMP_SCALE : 1);
    p.onRoof = false;
    p.houseId = 0;
    p.jumpBuffer = 0;
    p.coyote = 0;
    if (this.auto) this.autoHold = true;
    this.audio.jump();
  }

  private resolveRoofs(dt: number): void {
    const p = this.player;
    const feet = p.y + PLAYER_H / 2;
    const cx = p.x;

    if (p.onRoof) {
      const house = this.world.houseById(p.houseId);
      if (!house) {
        p.onRoof = false;
        return;
      }
      const b = roofBounds(house);
      p.y = b.y - PLAYER_H / 2;
      if (cx > b.right) {
        p.onRoof = false;
        p.houseId = 0;
        p.x = b.right + 6;
        p.vy = 90;
      }
      return;
    }

    if (p.vy < 0) return;

    const prevFeet = feet - p.vy * dt;

    for (const h of this.world.houses) {
      const b = roofBounds(h);
      if (cx < b.left + 2 || cx > b.right - 2) continue;
      const inflatable = h.obstacles.find((o) => o.kind === "inflatable");
      if (inflatable) {
        const ix = h.x + inflatable.x;
        if (cx >= ix && cx <= ix + inflatable.w && feet >= b.y - 20 && prevFeet <= b.y + 8) {
          this.beginFall("inflatable");
          return;
        }
      }
      const fromAbove = prevFeet <= b.y + 2;
      if (fromAbove && feet >= b.y - 28) {
        this.landOn(h);
        return;
      }
    }
  }

  private landOn(h: House): void {
    const p = this.player;
    const b = roofBounds(h);
    p.onRoof = true;
    p.houseId = h.id;
    p.y = b.y - PLAYER_H / 2;
    p.vy = 0;
    p.rotation = 0;
    this.landLock = LAND_LOCK;
    this.audio.land();
    this.particles.dust(p.x, b.y);

    if (!h.lit) {
      h.lit = true;
      const next = applyLanding(this.stats.score, this.stats.streak);
      this.stats.score = next.score;
      this.stats.streak = next.streak;
      this.stats.bestStreak = Math.max(this.stats.bestStreak, next.streak);
      this.stats.housesLit += 1;
      this.stats.energy = Math.min(ENERGY_MAX, this.stats.energy + ENERGY_REFILL);
      this.audio.lightUp(this.stats.housesLit);
      const color = h.lightStyle === "warm" ? "#ffd27a" : "#7ad0ff";
      this.particles.burst(h.x + h.w * 0.5, h.roofY - 10, color, 20);
      this.shake = 0.12;
    }
  }

  private resolveObstacles(): void {
    const p = this.player;
    if (!p.onRoof) return;
    const house = this.world.houseById(p.houseId);
    if (!house) return;
    for (const o of house.obstacles) {
      if (o.clipped || o.kind === "inflatable") continue;
      const ox = obstacleWorldX(house, o, this.time);
      const left = ox - 4;
      const right = ox + o.w + 4;
      const px = p.x;
      if (px < left || px > right) continue;
      if (o.kind === "ice") {
        this.clip(o, "ice");
        p.x += 18;
        if (p.x > roofBounds(house).right - 2) this.beginFall("ice");
        continue;
      }
      this.clip(o, o.kind);
      p.stumble = STUMBLE_TIME;
    }
  }

  private clip(o: { clipped: boolean }, _kind: string): void {
    if (o.clipped) return;
    o.clipped = true;
    this.stats.streak = 0;
    this.stats.clipped += 1;
    this.audio.clip();
    this.shake = 0.18;
    this.particles.burst(this.player.x, this.player.y, "#ffb070", 10);
  }

  private beginFall(_why: string): void {
    if (this.mode !== "playing") return;
    this.mode = "falling";
    this.player.onRoof = false;
    this.player.vy = 80;
    this.player.fallT = 0;
    this.audio.fall();
    this.shake = 0.3;
  }

  private updateFall(dt: number): void {
    const p = this.player;
    p.fallT += dt;
    p.vy += GRAVITY * 0.7 * dt;
    p.x += 40 * dt;
    p.y += p.vy * dt;
    p.rotation += 3.4 * dt;
    const targetCam = p.x - this.viewW * 0.3;
    this.cameraX += (targetCam - this.cameraX) * Math.min(1, dt * 4);
    if (p.fallT >= FALL_DURATION) {
      this.mode = "over";
      this.hooks.onGameOver(this.stats);
    }
  }

  private draw(): void {
    const ctx = this.ctx;
    const { w, h, scale } = resizeCanvas(this.canvas);
    this.viewW = w;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    const sx = this.shake > 0 ? (Math.random() - 0.5) * 10 * this.shake : 0;
    const sy = this.shake > 0 ? (Math.random() - 0.5) * 8 * this.shake : 0;
    ctx.translate(sx, sy);

    const progress = this.stats.housesLit + (this.mode === "start" ? 6 : 0);
    drawSky(ctx, w, h, progress, this.time);
    drawHills(ctx, this.cameraX, w, h, progress);
    drawFarHouses(ctx, this.cameraX, w, progress);
    drawGround(ctx, this.cameraX, w, h, progress);

    ctx.save();
    ctx.translate(-this.cameraX, 0);
    for (const house of this.world.houses) {
      if (house.x + house.w < this.cameraX - 40 || house.x > this.cameraX + w + 40) continue;
      if (house.lit) house.lightT = Math.min(1, house.lightT + 0.045);
      drawHouse(ctx, house, this.time);
    }
    this.particles.draw(ctx);
    if (this.mode !== "start") {
      drawPlayer(ctx, this.player, this.mode === "falling");
    }
    ctx.restore();

    drawHudGlow(ctx, w, h, progress);
  }
}

function emptyStats(): RunStats {
  return {
    housesLit: 0,
    streak: 0,
    bestStreak: 0,
    score: 0,
    energy: ENERGY_START,
    clipped: 0,
  };
}

function spawnPlayer(x: number, y: number): Player {
  return {
    x,
    y,
    vx: 0,
    vy: 0,
    onRoof: false,
    houseId: 0,
    facing: 1,
    runT: 0,
    jumpHeld: false,
    coyote: 0,
    jumpBuffer: 0,
    stumble: 0,
    fallT: 0,
    rotation: 0,
  };
}
